import { Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { PAYMENTS_ENABLED } from '../../lib/config'
import { SUBPROCESSORS, RETENTION_DAYS, LAST_UPDATED } from '../../content/trustFacts'

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-stone font-body text-navy">
      <nav className="sticky top-0 z-40 bg-navy text-stone px-6 py-3 flex items-center justify-between border-b-2 border-golden">
        <Link to="/" className="flex items-center gap-2 hover:text-stone/80 transition">
          <ArrowLeft size={18} />
          <span className="hl text-xl text-stone">Day One</span>
        </Link>
      </nav>

      <div className="max-w-3xl mx-auto px-6 py-12 md:py-16">
        <h1 className="hl text-[clamp(1.6rem,4.4vw,2.6rem)] mb-2">Privacy Policy</h1>
        <p className="mono-label text-navy/70 mb-10">Last updated: {LAST_UPDATED}</p>

        <div className="prose prose-navy max-w-none space-y-8 text-navy/80 leading-relaxed">
          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">Overview</h2>
            <p>
              Day One is an team onboarding workshop run by AI facilitators built by MVP Club Consulting.
              We take your privacy seriously. This policy explains what data we collect, how we use it,
              and your rights regarding that data.
            </p>
            <p className="font-semibold text-navy">
              We never sell your data. We do not share your data with third parties for marketing purposes.
            </p>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">Data We Collect</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Account information:</strong> Display names and email addresses provided during authentication via Google Sign-In or email and password. Every session, solo or team, is tied to an account with a verified email address. Teammates who join a room you host give only a display name.</li>
              <li><strong>Chat messages:</strong> All messages sent during workshop sessions, including messages from AI agents.</li>
              <li><strong>Uploaded documents:</strong> Files you upload during sessions (PDFs, Word docs, images) for AI review.</li>
              <li><strong>Reflection responses:</strong> Your independent answers during reflection rounds.</li>
              <li><strong>Session artifacts:</strong> Documents generated during the workshop (system prompts, next-steps files, etc.).</li>
              <li><strong>Session feedback:</strong> Ratings and comments submitted at the end of a session.</li>
            </ul>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">How We Use Your Data</h2>
            <p>Your data is used solely for:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Workshop functionality:</strong> Powering real-time chat, AI facilitation, document review, and session management.</li>
              <li><strong>Session continuity:</strong> Enabling returning teams to build on previous session work.</li>
              <li><strong>Performance analysis:</strong> Understanding how workshops run so we can improve the product.</li>
              {PAYMENTS_ENABLED && (
                <li><strong>Payment processing:</strong> Handling payments and billing records (processed by Stripe).</li>
              )}
            </ul>
            <p>We do not use your data for advertising, profiling, or any purpose beyond delivering the workshop experience.</p>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">AI Data Processing</h2>
            <p>
              During a workshop, the messages needed to answer each turn, along with the text of any
              document you upload, are sent to an AI provider to generate the facilitator's reply. There are
              two paths, and it is worth being precise about which is which.
            </p>
            <p>
              <strong>Primary path: OpenRouter to Modal.</strong> Nearly every turn goes to{' '}
              <strong>OpenRouter, Inc.</strong>, which routes it to <strong>Modal</strong>, the United States
              inference host that runs the GLM model we use. OpenRouter{' '}
              <strong>does not store prompts or completions by default</strong> and does not train on them;
              it keeps request metadata such as token counts and latency. The endpoint data policy OpenRouter
              publishes for Modal is zero prompt retention and no training. We pin routing to Modal and put
              Z.ai's own endpoint on our ignore list, so <strong>no session content is sent to Z.ai the
              company</strong>. See{' '}
              <a href="https://openrouter.ai/docs/guides/privacy/data-collection" target="_blank" rel="noopener noreferrer" className="text-amber hover:text-amber-dark underline">
                OpenRouter's data collection policy
              </a>.
            </p>
            <p>
              <strong>Fallback path: Anthropic.</strong> When the primary path fails or is too slow, the turn
              goes to Anthropic's Claude API instead. <strong>Anthropic does not train its models on data
              submitted through the API</strong> and, by default, deletes API inputs and outputs within 30
              days. We do not have a zero-data-retention agreement with Anthropic, so that 30-day default is
              what applies. See{' '}
              <a href="https://privacy.claude.com/en/articles/7996866-how-long-do-you-store-personal-data" target="_blank" rel="noopener noreferrer" className="text-amber hover:text-amber-dark underline">
                Anthropic's retention policy
              </a>.
            </p>
            <p>
              Web search is attached only on the Anthropic path. When an agent looks something up, it sends a
              short query derived from the conversation, never the transcript and never an uploaded document.
              Web search is switched off on the OpenRouter path.
            </p>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">Payment Processing</h2>
            {PAYMENTS_ENABLED ? (
              <p>
                Payments are processed by <strong>Stripe</strong>. We do not store your card numbers,
                bank account details, or other payment credentials. Stripe handles all payment data in
                accordance with PCI-DSS standards. See{' '}
                <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-amber hover:text-amber-dark underline">
                  Stripe's Privacy Policy
                </a>{' '}
                for details.
              </p>
            ) : (
              <p>
                Day One is free, so we collect no payment data at all. There is no card number, no
                billing address, and no payment processor involved.
              </p>
            )}
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">Cookies and Tracking</h2>
            <p>
              Day One uses only <strong>essential cookies</strong> required for Firebase authentication
              (session tokens). We do not use:
            </p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Advertising or behavioral analytics services</li>
              <li>Advertising cookies</li>
              <li>Third-party tracking pixels</li>
              <li>Fingerprinting or cross-site tracking</li>
            </ul>
            <p>
              We do use <strong>Sentry</strong> for error monitoring. When something breaks, Sentry
              receives technical error data (a stack trace, the URL path, and your browser and
              operating system version) so we can fix it. It is not used to profile you, track you
              across sites, or build an advertising audience.
            </p>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">Data Storage</h2>
            <p>
              All session data is stored in <strong>Google Firebase Firestore</strong>, located in the
              the United States (data at rest in us-east1, compute in us-central1). Data is encrypted at rest and in transit using industry-standard
              encryption protocols.
            </p>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">Data Retention</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Session data</strong> (chat messages, uploaded document text, reflections, artifacts, and feedback) is retained for <strong>{RETENTION_DAYS} days from the day the session was created</strong>, then deleted by a scheduled job that runs daily. This window exists so returning teams can build on previous sessions.</li>
              <li><strong>Account records</strong>, meaning your display name and email address, are kept for as long as your account is active.</li>
              <li><strong>You can delete your own data in the app at any time</strong>, without asking us. Deleting a session removes its messages, documents, and artifacts; deleting your account removes your account record along with them.</li>
              <li>If you email us a deletion request instead, we complete it <strong>within 30 days</strong>.</li>
            </ul>
            <p>
              One exception: where point-in-time recovery is enabled on our database, a deleted
              record may persist in recovery data until that window elapses. Recovery data is never
              used to serve the product and is not accessible to anyone but our operators.
              {PAYMENTS_ENABLED && ' Stripe also retains billing records independently as required by financial regulation.'}
            </p>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">Your Rights</h2>
            <p>You have the right to:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Access:</strong> Request a copy of all data associated with your account.</li>
              <li><strong>Deletion:</strong> Delete your sessions and your account directly in the app, or ask us to do it for you.</li>
              <li><strong>Correction:</strong> Request correction of inaccurate personal data.</li>
              <li><strong>Portability:</strong> Download your session data, artifacts, and chat history.</li>
            </ul>
            <p>
              Deletion is self-serve. You do not need our permission or our help. For anything else,
              or if you would rather we handle the deletion, contact us at{' '}
              <a href="mailto:privacy@mvpclub.ai" className="text-amber hover:text-amber-dark underline">
                privacy@mvpclub.ai
              </a>{' '}
              and we will complete the request within 30 days.
            </p>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">Third-Party Services</h2>
            <p>These are every service that processes your data on our behalf. All of them operate in the United States.</p>
            <ul className="list-disc pl-5 space-y-2">
              {SUBPROCESSORS.map(sub => (
                <li key={sub.name}>
                  <strong>{sub.name}</strong> ({sub.role}): {sub.data}.{' '}
                  <span className="text-navy/70">{sub.retention}</span>{' '}
                  <a href={sub.policyUrl} target="_blank" rel="noopener noreferrer" className="text-amber hover:text-amber-dark underline">
                    Their policy
                  </a>.
                </li>
              ))}
            </ul>
            <p>
              Each service processes data according to their own privacy policies and terms of service.
              A fuller breakdown, including exactly what each one receives, is on our{' '}
              <Link to="/security" className="text-amber hover:text-amber-dark underline">
                IT &amp; Security Guide
              </Link>, and the contractual terms covering all of it are in our pre-signed{' '}
              <Link to="/dpa" className="text-amber hover:text-amber-dark underline">
                Data Processing Agreement
              </Link>, which your organization can review or countersign without contacting us.
            </p>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">Changes to This Policy</h2>
            <p>
              We may update this privacy policy from time to time. Significant changes will be
              communicated through a notice on our website. Continued use of Day One after changes
              constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="hl text-lg text-navy border-b-2 border-navy pb-2 mb-4">Contact</h2>
            <p>
              Questions about this privacy policy? Contact us at{' '}
              <a href="mailto:privacy@mvpclub.ai" className="text-amber hover:text-amber-dark underline">
                privacy@mvpclub.ai
              </a>.
            </p>
            <p className="text-sm text-navy/70 mt-4">
              MVP Club Consulting<br />
              Denver, CO
            </p>
          </section>
        </div>
      </div>
    </div>
  )
}
